import React from 'react';
import { Button, Collapse, DatePicker, Input, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { DepartmentTasks, TaskItem, TaskStatus } from '../../types/docking';
import { generateTaskId } from '../../api/docking';
import moment from 'moment';

const { Panel } = Collapse;

const TASK_STATUS_OPTIONS: { label: string; value: TaskStatus }[] = [
  { label: '未开始', value: 'pending' },
  { label: '进行中', value: 'in_progress' },
  { label: '已完成', value: 'completed' },
];

function statusLabel(s: TaskStatus): string {
  return TASK_STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s;
}

function statusColor(s: TaskStatus): string {
  if (s === 'completed') return '#52c41a';
  if (s === 'in_progress') return '#1890ff';
  return '#999';
}

interface TaskAssignmentEditorProps {
  value: DepartmentTasks[];
  onChange: (val: DepartmentTasks[]) => void;
  readOnly?: boolean;
}

export function TaskAssignmentEditor(props: TaskAssignmentEditorProps): React.ReactElement {
  const { value, onChange, readOnly } = props;

  const updateDept = (deptIdx: number, patch: Partial<DepartmentTasks>) => {
    const next = [...value];
    next[deptIdx] = { ...next[deptIdx], ...patch };
    onChange(next);
  };

  const removeDept = (deptIdx: number) => {
    onChange(value.filter((_, i) => i !== deptIdx));
  };

  const addDept = () => {
    onChange([...value, { department: '', tasks: [] }]);
  };

  const updateTask = (deptIdx: number, taskIdx: number, patch: Partial<TaskItem>) => {
    const dept = value[deptIdx];
    const tasks = [...dept.tasks];
    tasks[taskIdx] = { ...tasks[taskIdx], ...patch };
    updateDept(deptIdx, { tasks });
  };

  const removeTask = (deptIdx: number, taskIdx: number) => {
    const dept = value[deptIdx];
    updateDept(deptIdx, { tasks: dept.tasks.filter((_, i) => i !== taskIdx) });
  };

  const addTask = (deptIdx: number) => {
    const dept = value[deptIdx];
    const newTask: TaskItem = {
      id: generateTaskId(),
      description: '',
      assignee: '',
      deadline: null,
      status: 'pending',
    };
    updateDept(deptIdx, { tasks: [...dept.tasks, newTask] });
  };

  if (readOnly) {
    if (value.length === 0) return <span style={{ color: 'rgba(255,255,255,0.45)' }}>暂无任务分工</span>;
    return (
      <Collapse
        ghost
        className="docking-task-collapse"
        defaultActiveKey={value.map((_, i) => String(i))}
      >
        {value.map((dept, di) => (
          <Panel
            key={String(di)}
            header={<span style={{ color: 'rgba(255,255,255,0.85)' }}>{dept.department || '(未命名部门)'}</span>}
          >
            {dept.tasks.length === 0 ? (
              <span style={{ color: 'rgba(255,255,255,0.45)' }}>暂无任务</span>
            ) : (
              <div className="docking-task-list">
                {dept.tasks.map((task) => (
                  <div key={task.id} className="docking-task-row docking-task-row--readonly">
                    <span className="docking-task-desc">{task.description || '—'}</span>
                    <span className="docking-task-assignee">{task.assignee || '—'}</span>
                    <span className="docking-task-deadline">{task.deadline || '—'}</span>
                    <span className="docking-task-status" style={{ color: statusColor(task.status) }}>
                      {statusLabel(task.status)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        ))}
      </Collapse>
    );
  }

  return (
    <div className="docking-task-editor">
      {value.map((dept, di) => (
        <div key={di} className="docking-task-dept">
          <div className="docking-task-dept-header">
            <Input
              placeholder="部门名称"
              value={dept.department}
              onChange={(e) => updateDept(di, { department: e.target.value })}
              style={{ flex: 1 }}
            />
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeDept(di)}
              size="small"
            />
          </div>
          {dept.tasks.map((task, ti) => (
            <div key={task.id} className="docking-task-row">
              <Input
                placeholder="任务描述"
                value={task.description}
                onChange={(e) => updateTask(di, ti, { description: e.target.value })}
                style={{ flex: 2 }}
              />
              <Input
                placeholder="责任人"
                value={task.assignee}
                onChange={(e) => updateTask(di, ti, { assignee: e.target.value })}
                style={{ width: 90 }}
              />
              <DatePicker
                placeholder="截止日期"
                value={task.deadline ? moment(task.deadline) : null}
                onChange={(d) => updateTask(di, ti, { deadline: d ? d.format('YYYY-MM-DD') : null })}
                style={{ width: 130 }}
              />
              <Select
                value={task.status}
                onChange={(v) => updateTask(di, ti, { status: v })}
                options={TASK_STATUS_OPTIONS}
                style={{ width: 100 }}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeTask(di, ti)}
                size="small"
              />
            </div>
          ))}
          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => addTask(di)}
            style={{ marginTop: 4, width: '100%' }}
          >
            添加任务
          </Button>
        </div>
      ))}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addDept}
        style={{ width: '100%', marginTop: 8 }}
      >
        添加部门
      </Button>
    </div>
  );
}
